import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Loader2, TrendingUp, ArrowLeftRight } from 'lucide-react';
import CreateSwapOnBehalfDialog from '../components/admin/CreateSwapOnBehalfDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// Rates to 2 decimals (stored values can carry float noise, e.g. 14.499999999999998).
const pct = (v) => `${Number(v || 0).toFixed(2)}%`;

const zar = (n) => `R ${Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_CLS = {
  active: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  pending_approval: 'bg-amber-100 text-amber-800 border-amber-200',
  pending_listing_approval: 'bg-amber-100 text-amber-800 border-amber-200',
  withdrawn_settled: 'bg-gray-100 text-gray-700 border-gray-200',
  removed_by_admin: 'bg-gray-100 text-gray-700 border-gray-200',
  rejected: 'bg-red-100 text-red-800 border-red-200',
  executing: 'bg-amber-100 text-amber-800 border-amber-200',
  accepted: 'bg-blue-100 text-blue-800 border-blue-200',
  cancelled: 'bg-gray-100 text-gray-700 border-gray-200',
  settled: 'bg-slate-100 text-slate-700 border-slate-200',
};

export default function AdminMarketplacePage() {
  const { token } = useAuth();
  const [listings, setListings] = useState([]);
  const [carries, setCarries] = useState([]);
  const [summary, setSummary] = useState({ total_accrued: 0, total_paid: 0 });
  const [loading, setLoading] = useState(true);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      setLoading(true);
      const [l, c] = await Promise.all([
        axios.get(`${API_URL}/api/admin/listings`, authHeader),
        axios.get(`${API_URL}/api/admin/carry-positions`, authHeader),
      ]);
      setListings(l.data.items || []);
      setCarries(c.data.items || []);
      setSummary(c.data.summary || { total_accrued: 0, total_paid: 0 });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  // Swaps agreed on the marketplace wait for admin approval (2026-10-21).
  const decideSwap = async (listing, action) => {
    const offer = (listing.offers || []).find(o => o.id === listing.pending_offer_id) || {};
    const msg = action === 'approve'
      ? `Approve and execute the swap of ${listing.loan_id}?\n\nSeller ${listing.seller_rp_code} (${pct(listing.seller_rate)}) → buyer ${offer.buyer_rp_code || '—'} at ${pct(offer.offered_rate)}.\nThis moves the tranche in the loan book.`
      : `Decline the swap of ${listing.loan_id}? The listing goes back on the marketplace.`;
    if (!window.confirm(msg)) return;
    const note = window.prompt('Note for the audit trail (optional)') || null;
    try {
      const r = await axios.post(`${API_URL}/api/admin/listings/${listing.id}/${action}`, { note }, authHeader);
      toast.success(r.data?.message || 'Done');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  // New listings wait for admin approval before they are on the marketplace (2026-10-22).
  const decideListing = async (listing, action) => {
    const msg = action === 'publish'
      ? `Approve the listing of ${listing.loan_id} by ${listing.seller_rp_code} at ${pct(listing.listing_rate)}? It goes on the marketplace.`
      : action === 'remove'
        ? `Remove the listing of ${listing.loan_id} (${listing.seller_rp_code}) from the marketplace? Open offers are closed; nothing in the loan book changes.`
        : `Reject the listing of ${listing.loan_id}?`;
    if (!window.confirm(msg)) return;
    const note = window.prompt('Note for the audit trail (optional)') || null;
    try {
      const r = await axios.post(`${API_URL}/api/admin/listings/${listing.id}/${action}`, { note }, authHeader);
      toast.success(r.data?.message || 'Done');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const undoSwap = async (listing) => {
    if (!listing?.id) return;
    if (!window.confirm(`Undo swap for ${listing.loan_id}?\n\nThis will:\n • Restore the seller's original tranche\n • Delete the buyer's new tranche (${listing.new_loan_id || '—'})\n • Delete related carry positions & investor transactions\n • Delete this listing entirely\n\nThis cannot be undone.`)) return;
    try {
      const r = await axios.post(`${API_URL}/api/admin/listings/${listing.id}/undo-swap`, {}, authHeader);
      toast.success(r.data?.message || 'Swap reversed');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to undo'); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-marketplace">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-[#1D2D2A] font-['Manrope']">Marketplace & Carry</h1>
          <p className="text-[#6C757D] mt-1">All investor swap listings + active carry positions.</p>
        </div>
        <CreateSwapOnBehalfDialog
          onCreated={load}
          trigger={
            <Button className="bg-[#1D2D2A] hover:bg-[#243D37] text-white" data-testid="marketplace-create-swap-btn">
              <ArrowLeftRight className="h-4 w-4 mr-2" />
              Create swap on behalf of
            </Button>
          }
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile label="Active listings" value={listings.filter(l => l.status === 'active').length} />
        <Tile label="Awaiting approval" value={listings.filter(l => l.status === 'pending_approval' || l.status === 'pending_listing_approval').length} />
        <Tile label="Carry accrued" value={zar(summary.total_accrued)} />
        <Tile label="Carry paid" value={zar(summary.total_paid)} />
      </div>

      <Tabs defaultValue="listings">
        <TabsList>
          <TabsTrigger value="listings">Listings ({listings.length})</TabsTrigger>
          <TabsTrigger value="carries">Carry positions ({carries.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="listings" className="mt-4">
          <Card><CardContent className="pt-6">
            {loading ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : listings.length === 0 ? <div className="py-10 text-center text-[#6C757D]">No listings yet.</div> : (
              <Table>
                <TableHeader><TableRow><TableHead>Loan</TableHead><TableHead>Seller</TableHead><TableHead>Buyer(s)</TableHead>
                  <TableHead className="text-right">Price</TableHead><TableHead className="text-right">Rates</TableHead>
                  <TableHead className="text-right">Carry</TableHead><TableHead>Status</TableHead><TableHead>New loan</TableHead><TableHead /></TableRow></TableHeader>
                <TableBody>
                  {listings.map(l => (
                    <TableRow key={l.id} data-testid={`admin-listing-${l.id}`}>
                      <TableCell className="font-mono text-xs">{l.loan_id}</TableCell>
                      <TableCell><span className="font-mono text-xs">{l.seller_rp_code}</span></TableCell>
                      <TableCell className="text-xs">{(l.offers || []).map(o => o.buyer_rp_code).join(', ') || '—'}</TableCell>
                      <TableCell className="text-right font-mono">{zar(l.final_accrued_price || l.accrued_principal_at_listing)}</TableCell>
                      <TableCell className="text-right text-xs">{pct(l.listing_rate)}–{pct(l.seller_rate)}</TableCell>
                      <TableCell className="text-right font-mono">{l.carry_rate ? pct(l.carry_rate) : '—'}</TableCell>
                      <TableCell><Badge className={`${STATUS_CLS[l.status] || ''} border`}>{l.status}</Badge></TableCell>
                      <TableCell className="font-mono text-xs">{l.new_loan_id || '—'}</TableCell>
                      <TableCell>
                        {['active', 'pending_approval'].includes(l.status) && (
                          <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 border-red-200 mr-2" onClick={() => decideListing(l, 'remove')} data-testid={`remove-listing-${l.id}`}>Remove</Button>
                        )}
                        {l.status === 'pending_listing_approval' && (
                          <div className="flex gap-2">
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => decideListing(l, 'publish')} data-testid={`publish-listing-${l.id}`}>Approve listing</Button>
                            <Button size="sm" variant="outline" onClick={() => decideListing(l, 'reject-listing')} data-testid={`reject-listing-${l.id}`}>Reject</Button>
                          </div>
                        )}
                        {l.status === 'pending_approval' && (
                          <div className="flex gap-2">
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => decideSwap(l, 'approve')} data-testid={`approve-swap-${l.id}`}>Approve</Button>
                            <Button size="sm" variant="outline" onClick={() => decideSwap(l, 'decline')} data-testid={`decline-swap-${l.id}`}>Decline</Button>
                          </div>
                        )}
                        {l.status === 'accepted' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:bg-red-50 border-red-200"
                            onClick={() => undoSwap(l)}
                            data-testid={`undo-swap-${l.id}`}
                          >
                            Undo
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="carries" className="mt-4">
          <Card><CardContent className="pt-6">
            {loading ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : carries.length === 0 ? <div className="py-10 text-center text-[#6C757D]">No carry positions yet.</div> : (
              <Table>
                <TableHeader><TableRow><TableHead>Beneficiary</TableHead><TableHead>Source loan</TableHead>
                  <TableHead>Original</TableHead><TableHead className="text-right">Carry rate</TableHead>
                  <TableHead className="text-right">Principal</TableHead><TableHead className="text-right">Accrued</TableHead>
                  <TableHead className="text-right">Paid</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {carries.map(c => (
                    <TableRow key={c.id} data-testid={`admin-carry-${c.id}`}>
                      <TableCell className="font-mono text-xs">{c.beneficiary_rp_code}</TableCell>
                      <TableCell className="font-mono text-xs">{c.source_loan_id}</TableCell>
                      <TableCell className="font-mono text-xs text-[#6C757D]">{c.original_loan_id}</TableCell>
                      <TableCell className="text-right font-mono">{pct(c.carry_rate)}</TableCell>
                      <TableCell className="text-right font-mono">{zar(c.carry_principal)}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-700">{zar(c.carry_accrued)}</TableCell>
                      <TableCell className="text-right font-mono">{zar(c.carry_paid)}</TableCell>
                      <TableCell><Badge className={`${STATUS_CLS[c.status] || ''} border`}>{c.status}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="rounded-lg border bg-white p-4">
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-[#6C757D] uppercase tracking-wide">{label}</span>
        <TrendingUp className="h-5 w-5 text-[#18A37C]" />
      </div>
      <div className="mt-2 text-xl font-bold text-[#1D2D2A]">{value}</div>
    </div>
  );
}
